import { Auth } from "aws-amplify";
import Api from "../util/api";
import { useState } from "react";
import { useUserContext } from "../context/UserContextProvider";
import { useCompanyStore } from "../stores/useCompanyStore";
import { usePoliciesContext } from "../context/PoliciesContextProvider";
import { useLocation, useSearchParams } from "react-router-dom";
import { AppRoutes } from "../constants/routes";
import { setSelectedCompanyMembership } from "../util/company-utils";
import { useNavigateParams } from "./useNavigateParams";
import { SubscriptionOptionEnum } from "@fena/toolkit-types";

export const useAuthCheck = () => {
  const navigate = useNavigateParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const { setUser } = useUserContext();
  const {
    company,
    setCompany,
    refreshSettings,
    setParentCustomizationSettings,
  } = useCompanyStore();
  const { setPolicies } = usePoliciesContext();

  const checkSignInStatus = async () => {
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getIdToken();
      const jwt = accessToken.getJwtToken();

      Api.updateHeadersWithToken(jwt);
      const user = await Api.getUser();
      const selectedMembership = setSelectedCompanyMembership(
        user.companyMemberships
      );

      if (!selectedMembership) {
        navigate(AppRoutes.Open.Auth.SUSPENDED_USER);
        // crutch
        return { user: null };
      }

      const companyData = await Api.getCompany();

      setUser({
        ...user,
        cognitoReferenceId: accessToken.payload.sub,
        selectedCompanyMembership: selectedMembership,
      } as any);

      const policiesAsMap = new Map(
        Object.entries(selectedMembership.role.policies)
      );
      setPolicies(policiesAsMap);

      const parentCompanyCustoimzationSettings =
        await Api.getParentCustomizationSettings();
      setParentCustomizationSettings(parentCompanyCustoimzationSettings);

      const currentSubscription = await Api.getCurrentSubscriptions();
      const b2bSubscription = currentSubscription?.data?.filter(
        (el: any) =>
          el?.subscriptionOption ===
          SubscriptionOptionEnum.SHOPIFY_B2B_SUBSCRIPTION
      )[0];

      const currentSubscriptionObj = currentSubscription.data
        ?.filter(
          (el: any) =>
            el?.details?.subscriptionOption ===
              SubscriptionOptionEnum.PAYMENTS_ONLY ||
            el?.details?.subscriptionOption === SubscriptionOptionEnum.TOOLKIT
        )
        .sort((a: any, b: any) => {
          return (
            new Date(String(b?.createdAt)).getTime() -
            new Date(String(a?.createdAt)).getTime()
          );
        })[0];

      setCompany({
        ...companyData,
        currentSubscription: currentSubscriptionObj,
        b2bSubscription,
      });
      await refreshSettings();
      setLoading(false);
      if (
        selectedMembership.role.isDefaultRole &&
        selectedMembership.role.name === "Warehouse Picker"
      ) {
        navigate(
          AppRoutes.Private.Shipments.SHIPMENTS_PICKER_LIST,
          searchParams
        );
      }
      return {
        user,
        companyData: {
          ...companyData,
          currentSubscription: currentSubscriptionObj,
        },
      };
    } catch (e: any) {
      setLoading(false);
      console.error(e);
      if (
        !location.pathname.includes("/sign-up/invite") &&
        !location.pathname.includes("/verify/") &&
        !location.pathname.includes("/redirects") &&
        !location.pathname.includes(AppRoutes.Open.Apps.SIGN_IN) &&
        !location.pathname.includes(AppRoutes.Open.Apps.SIGN_UP) &&
        !location.pathname.includes(AppRoutes.Open.Apps.PASSWORD_RESET) &&
        !location.pathname.includes(
          AppRoutes.Open.Auth.EMAIL_VERIFICATION_SUBMIT
        ) &&
        !location.pathname.includes(AppRoutes.Open.Auth.SIGN_UP_START)
      ) {
        navigate(AppRoutes.Open.Auth.SIGN_IN_START, searchParams);
      }

      return { user: null };
    }
  };

  return { loading, checkSignInStatus, company };
};
